import { IonButton, IonIcon } from '@ionic/react';
import classnames from 'classnames';
import { personAddOutline as waitingListIcon, sendOutline as sendIcon } from 'ionicons/icons';
import { ComponentProps } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BookingParams, EventInfo } from '.';

export function formatPrice(cost: number, locale = 'de-DE') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
  }).format(cost);
}

const ParticipantsButton = (btnProps: ComponentProps<'button'>) => (
  <button
    {...btnProps}
    type="button"
    className="text-lg font-bold h-8 w-8 bg-primary-default text-primary-contrast rounded-full flex items-center justify-center"
  />
);

type ParticularsSelectionFormProps = {
  eventPriceVariants?: {
    id: string;
    displayName: string;
    price: number | null;
  }[];
  numParticipants: number;
  onParticipantsChange: (numParticipants: number) => void;
  onCommentUpdate: (comment: string) => void;
  onBookingParamsChange: (params: BookingParams) => void;
  bookingParams: BookingParams;
  maxParticipants: number | null | undefined;
  onSubmitBooking: () => void;
  eventInfo: EventInfo;
  isTimeSlotEnabledEvent: boolean;
  updateSuccessMsg: (msg: string) => void;
  ownerAuthId?: string;
  showSubmitButton: boolean;
  renderOnTopOfForm?: () => React.ReactNode;
  showExtraFields?: boolean;
};

export function determineWillBePutOnWaitingList(
  numNewParticipants: number,
  maxParticipants?: number
): boolean {
  return maxParticipants ? numNewParticipants > maxParticipants : false;
}

export type AdditionalFormRenderingProps = {
  /**
   * Attention: This will hide the partipants picker
   */
  renderOnTopOfForm?: () => React.ReactNode;
};

const ParticularsSelectionForm = ({
  eventPriceVariants,
  onParticipantsChange,
  numParticipants = 0,
  bookingParams,
  onBookingParamsChange,
  eventInfo,
  onSubmitBooking,
  isTimeSlotEnabledEvent,
  maxParticipants,
  updateSuccessMsg,
  onCommentUpdate,
  ownerAuthId,
  showSubmitButton,
  renderOnTopOfForm,
  showExtraFields = true,
}: ParticularsSelectionFormProps & AdditionalFormRenderingProps) => {
  const { t } = useTranslation();

  const potentialNewNumParticipants =
    numParticipants + (bookingParams.selectedTimeSlot?.numOfConfirmedParticipants ?? 0);

  const willBePutOnWaitingList = determineWillBePutOnWaitingList(
    potentialNewNumParticipants,
    maxParticipants || undefined
  );

  const translationState = {
    initial: {
      btnContent: t('book-now'),
      successMsg: 'the-event-was-booked-successfully',
    },
    bookable_on_request: {
      btnContent: t('send-request'),
      successMsg: 'booking-request-was-sent',
    },
    waiting_list: {
      btnContent: t('get-on-waitinglist'),
      successMsg: 'you-are-on-the-waitling-list-and-will-be-notified-as-soon-there-is-a-free-place',
    },
  };

  let bookBtnContent = translationState.initial.btnContent;

  if (eventInfo?.bookability === 'bookable_on_request') {
    bookBtnContent = translationState.bookable_on_request.btnContent;
    updateSuccessMsg(translationState.bookable_on_request.successMsg);
  }

  if (willBePutOnWaitingList) {
    if (isTimeSlotEnabledEvent) {
      bookBtnContent = translationState.initial.btnContent;
      updateSuccessMsg(translationState.initial.successMsg);
    } else {
      bookBtnContent = translationState.waiting_list.btnContent;
      updateSuccessMsg(translationState.waiting_list.successMsg);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const noop = () => {};
  console.log('GuestQuantityPicker', numParticipants);

  return (
    <div className="my-4">
      <h1 className="text-xl font-bold text-center">
        <Trans i18nKey="guests-and-extras"></Trans>{' '}
      </h1>
      {renderOnTopOfForm?.()}
      {(!maxParticipants || maxParticipants > 1) && !renderOnTopOfForm && (
        <div className="mt-6 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-bold">
              <Trans>participants</Trans>
            </h2>
            <div className="flex items-center gap-2 mt-2">
              <ParticipantsButton
                onClick={() => {
                  onParticipantsChange(numParticipants - 1);
                }}
              >
                -
              </ParticipantsButton>
              <span className="text-lg">{numParticipants}</span>
              <ParticipantsButton
                onClick={() => {
                  onParticipantsChange(numParticipants + 1);
                }}
              >
                +
              </ParticipantsButton>
            </div>
          </div>
        </div>
      )}

      {eventPriceVariants && (
        <div className="mt-6">
          <h2 className="text-lg font-bold">
            <Trans>extras</Trans>
          </h2>
          <div className="space-y-4 mt-4">
            {eventPriceVariants.map((priceVariant) => {
              const isSelected = bookingParams?.priceVariants?.includes(priceVariant.id);

              function onToggle() {
                if (isSelected) {
                  onBookingParamsChange({
                    ...bookingParams,
                    priceVariants: bookingParams?.priceVariants?.filter(
                      (priceVariantId) => priceVariantId !== priceVariant.id
                    ),
                  });
                } else {
                  onBookingParamsChange({
                    ...bookingParams,
                    priceVariants: [...(bookingParams?.priceVariants ?? []), priceVariant.id],
                  });
                }
              }

              return (
                <div key={priceVariant.id} className="flex items-center justify-between">
                  <button
                    className={classnames(
                      'border-2 rounded-full border-primary-default py-1 px-2 text-sm',
                      {
                        'bg-primary-default text-primary-contrast': isSelected,
                      }
                    )}
                    type="button"
                    onClick={onToggle}
                  >
                    {priceVariant.displayName}
                  </button>
                  {priceVariant.price && priceVariant.price > 0 && (
                    <span className="text-gray-400">{formatPrice(priceVariant.price)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showExtraFields && (
        <div className="mt-6">
          <p className="mb-2">
            <Trans>extra-wishes</Trans>
          </p>
          <textarea
            onChange={(e) => onCommentUpdate(e.target.value)}
            value={bookingParams.comment}
            className="rounded-md text-black bg-light-default focus:outline-none border-gray-400 min-h-[60px] w-full"
            placeholder={t('add-a-comment-to-your-booking-request')}
          />
        </div>
      )}

      {showSubmitButton && (
        <div className="sticky bottom-4 mt-4">
          <IonButton
            color={'primary'}
            expand="block"
            fill="solid"
            shape="round"
            onClick={() => {
              isTimeSlotEnabledEvent
                ? willBePutOnWaitingList
                  ? noop() //Todo: @maxim what's happening here
                  : onSubmitBooking()
                : onSubmitBooking();
            }}
            disabled={isTimeSlotEnabledEvent ? (willBePutOnWaitingList ? true : false) : false}
          >
            {bookBtnContent}
            <IonIcon
              slot="end"
              icon={
                !isTimeSlotEnabledEvent
                  ? willBePutOnWaitingList
                    ? waitingListIcon
                    : sendIcon
                  : sendIcon
              }
            />
          </IonButton>
        </div>
      )}
    </div>
  );
};

export default ParticularsSelectionForm;
